import React, { createContext, useContext, useState, ReactNode } from 'react';

export type PendingNavigationType = 'route' | 'logout' | 'password';

export interface PendingNavigation {
    type: PendingNavigationType;
    target?: string;
}

interface NavigationBlockerContextType {
    shouldBlockNavigation: boolean;
    setShouldBlockNavigation: (shouldBlock: boolean) => void;
    getBlockMessage: () => string;
    setBlockMessage: (message: string) => void;
    // Dialog state
    isDialogOpen: boolean;
    dialogTitle: string;
    dialogMessage: string;
    showDialog: (title: string, message: string) => void;
    hideDialog: () => void;
    // Pending navigation
    pendingNavigation: PendingNavigation | null;
    setPendingNavigation: (action: PendingNavigation | null) => void;
    executePendingNavigation: () => PendingNavigation | null;
    clearPendingNavigation: () => void;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextType | undefined>(undefined);

export const NavigationBlockerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);
    const [blockMessage, setBlockMessageState] = useState('You have unsaved changes that are being saved. Please wait for the save to complete before navigating away.');
    
    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    
    // Pending navigation state
    const [pendingNavigation, setPendingNavigationState] = useState<PendingNavigation | null>(null);

    const setBlockMessage = (message: string) => {
        setBlockMessageState(message);
    };

    const getBlockMessage = () => blockMessage;

    const showDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMessage(message);
        setIsDialogOpen(true);
    };

    const hideDialog = () => {
        setIsDialogOpen(false);
    };

    const setPendingNavigation = (action: PendingNavigation | null) => {
        setPendingNavigationState(action);
    };

    const executePendingNavigation = (): PendingNavigation | null => {
        const pending = pendingNavigation;
        if (pending) {
            // Clear blocking state
            setShouldBlockNavigation(false);
            // Clear pending navigation
            setPendingNavigationState(null);
            // Close dialog
            hideDialog();
        }
        return pending;
    };

    const clearPendingNavigation = () => {
        setPendingNavigationState(null);
    };

    const value = {
        shouldBlockNavigation,
        setShouldBlockNavigation,
        getBlockMessage,
        setBlockMessage,
        isDialogOpen,
        dialogTitle,
        dialogMessage,
        showDialog,
        hideDialog,
        pendingNavigation,
        setPendingNavigation,
        executePendingNavigation,
        clearPendingNavigation,
    };

    return (
        <NavigationBlockerContext.Provider value={value}>
            {children}
        </NavigationBlockerContext.Provider>
    );
};

export const useNavigationBlocker = () => {
    const context = useContext(NavigationBlockerContext);
    if (context === undefined) {
        throw new Error('useNavigationBlocker must be used within a NavigationBlockerProvider');
    }
    return context;
};

